import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import TimelineOppositeContent from '@material-ui/lab/TimelineOppositeContent';
import { formatDatetime } from 'utilities/format';
import moment from 'moment-timezone';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { request } from 'utilities/graph';
import { updateIssue, updateIssueComment } from 'graphql/mutations';
import cache from 'utilities/cache';
import { TIME_ZONE, issueTypes as allIssueTypes } from '@silvergatedelivery/constants';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 0,
  },
  itemContainer: {
    minHeight: 50,
    padding: 0,
  },
  listSubHeader: {
    fontWeight: 700,
    borderBottom: '1px solid #E5E5E5',
  },
  datetimeContainer: {
    maxWidth: 200,
  },
  issueTypeContainer: {
    marginLeft: 50,
  },
}));
export default function IssueTimeline({ issue: inIssue, comments: inComments = [], setIssueUpdated }) {
  const classes = useStyles();
  const [editIndex, setEditIndex] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editDatetime, setEditDatetime] = useState();
  const [issue, setIssue] = useState(inIssue);
  const [comments, setComments] = useState(inComments);
  const [editType, setEditType] = useState(null);

  useEffect(() => {
    setIssue(inIssue);
    setComments(inComments);
  }, [inIssue, inComments]);

  let currentDate;
  const data = [issue, ...comments.sort((a, b) => moment(a.executedAt || a.createdAt).diff(moment(b.executedAt || b.createdAt)))];
  const updatedData = data.map((x) => {
    const format = formatDatetime(x.executedAt || x.createdAt, { year: false, weekday: false });
    const [date, time] = format.split(' ');

    if (date === currentDate) {
      x.displayDatetime = time;
    } else {
      currentDate = date;
      x.displayDatetime = format;
    }

    return x;
  });

  if (!issue) return null;

  const handleEditClick = (index, content, datetime) => {
    setEditIndex(index);
    setEditContent(content);
    setEditDatetime(datetime);
    setEditType(null);
  };

  const handleEditTypeClick = (type) => {
    setEditType(type);
    setEditIndex(null);
  };

  const handleSaveClick = async (index) => {
    const username = cache.get('app:username');
    const now = new Date().toISOString();
    if (index === 0) {
      issue.description = editContent;
      issue.executedAt = editDatetime;
      const input = {
        id: issue.id,
        description: editContent,
        executedAt: editDatetime,
        updatedAt: now,
        updatedBy: username,
      };
      const { data: { updateIssue: updatedIssue } } = await request(updateIssue, { input });
      setIssue(updatedIssue);
    } else {
      const originalContent = comments[index - 1].content;
      const statusChangePattern = /(\(狀態：.* => .*\))$/;
      const statusChangeMatch = originalContent.match(statusChangePattern);
      const newContent = editContent + (statusChangeMatch ? ` ${statusChangeMatch[0]}` : '');
      comments[index - 1].content = newContent;
      comments[index - 1].executedAt = editDatetime;
      const input = {
        id: comments[index - 1].id,
        content: newContent,
        executedAt: editDatetime,
        updatedAt: now,
        updatedBy: username,
      };
      const { data: { updateIssueComment: updatedComment } } = await request(updateIssueComment, { input });
      const updatedComments = comments.map((comment) => {
        if (comment.id === updatedComment.id) {
          return updatedComment;
        }
        return comment;
      });
      setComments(updatedComments);
    }
    setIssueUpdated && setIssueUpdated(true);
    setEditIndex(null);
  };

  const handleSaveTypeClick = async () => {
    const username = cache.get('app:username');
    const now = new Date().toISOString();
    const input = {
      id: issue.id,
      type: editType,
      updatedAt: now,
      updatedBy: username,
    };
    const { data: { updateIssue: updatedIssue } } = await request(updateIssue, { input });
    setIssue(updatedIssue);
    setIssueUpdated && setIssueUpdated(true);
    setEditType(null);
  };

  const render = (item, index) => {
    const isStatusChangeOnly = (content) => {
      const statusChangePattern = /^狀態：.* => .*/;
      return statusChangePattern.test(content.trim());
    };

    const extractEditableContent = (content) => {
      const statusChangePattern = /\(狀態：.* => .*\)$/;
      const match = content.match(statusChangePattern);
      if (match) {
        return content.replace(statusChangePattern, '').trim();
      }
      return content;
    };

    if (index === 0) {
      return (
        <React.Fragment>
          {editIndex === index ? (
            <Box display="flex" alignItems="center">
              <TextField
                margin="dense"
                label="追蹤事項"
                type="text"
                value={editContent}
                fullWidth
                multiline
                minRows={2}
                onChange={(e) => setEditContent(e.target.value)}
              />
              <IconButton onClick={() => handleSaveClick(index)}>
                <SaveIcon />
              </IconButton>
              <IconButton onClick={() => setEditIndex(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="body2">
              <Box display="flex" alignItems="center">
                {issue.description.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
                <IconButton
                  onClick={() => handleEditClick(index, issue.description, issue.executedAt)}
                >
                  <EditIcon/>
                </IconButton>
              </Box>
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            {issue.reportedBy}
          </Typography>
        </React.Fragment>
      );
    } else {
      const editableContent = extractEditableContent(item.content);
      return (
        <React.Fragment>
          {editIndex === index ? (
            <Box display="flex" alignItems="center">
              <TextField
                margin="dense"
                label="處理筆記"
                type="text"
                value={editContent}
                fullWidth
                multiline
                minRows={2}
                onChange={(e) => setEditContent(e.target.value)}
              />
              <IconButton onClick={() => handleSaveClick(index)}>
                <SaveIcon />
              </IconButton>
              <IconButton onClick={() => setEditIndex(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="body2">
              <Box display="flex" alignItems="center">
                {item.content.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
                {!isStatusChangeOnly(item.content) && (
                  <IconButton
                    onClick={() => handleEditClick(index, editableContent, item.executedAt)}
                  >
                    <EditIcon/>
                  </IconButton>
                )}
              </Box>
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            {item.username}
          </Typography>
        </React.Fragment>
      );
    }
  };

  return (
    <Timeline className={classes.container}>
      <Box display="flex" alignItems="center" className={classes.issueTypeContainer}>
        {editType ?
          <>
            <FormControl>
              <InputLabel>類別</InputLabel>
              <Select
                value={editType}
                onChange={(event) => {
                  setEditType(event.target.value);
                }}
              >
                {allIssueTypes.map(({ label, value }) => (
                  <MenuItem key={label} value={value} >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={() => handleSaveTypeClick(editType)}>
              <SaveIcon />
            </IconButton>
            <IconButton onClick={() => setEditType(null)}>
              <CloseIcon />
            </IconButton>
          </>:
          <>
            <Typography color="textPrimary" variant="h6">
              {issue.type}
            </Typography>
            {allIssueTypes.some(({ value }) => value === issue.type) &&
            <IconButton onClick={() => handleEditTypeClick(issue.type)}>
              <EditIcon />
            </IconButton>}
          </>
        }
      </Box>
      {updatedData.map((item, index) => (
        <TimelineItem key={index} className={classes.itemContainer}>
          <TimelineOppositeContent className={classes.datetimeContainer}>
            {editIndex === index ? (
              <TextField
                label='時間'
                type="datetime-local" // format 2017-05-24T10:30
                value={moment(editDatetime).tz(TIME_ZONE).format('YYYY-MM-DDTHH:mm')}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                onChange={(e) => {
                  const updatedDatetime = moment.tz(e.target.value, TIME_ZONE).toISOString();
                  if (updatedDatetime) {
                    setEditDatetime(updatedDatetime);
                  }
                }}
              />
            ) : (
              <Typography color="textSecondary" variant="body2">
                {item.displayDatetime}
              </Typography>
            )}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color={index === data.length - 1 ? 'primary' : undefined} />
            {(index + 1 < data.length) && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            {render(item, index)}
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
}

IssueTimeline.propTypes = {
  issue: PropTypes.object,
  comments: PropTypes.array,
  setIssueUpdated: PropTypes.func,
};
